<template>
  <l-map ref="lightMap" :zoom="simple.zoom" :options="{ zoomControl: false }" :center="simple.center" :bounds="bounds"
    @ready="onMapReady" :style="{
      boxShadow: '0px 3px 6px rgba(191, 197, 219, 0.08)',
      zIndex: 1,
      borderRadius: '8px',
    }"
    class="w-full h-full rounded-[8px] shadow-sm"
  >
    <l-tile-layer :url="simple.url" :attribution="simple.attribution" :subdomains="simple.subdomains"
      :max-zoom="simple.maxZoom"></l-tile-layer>

    <l-control position="topleft" class="custom-tailwind-control">
      <div class="flex flex-col items-center justify-center">
        <div class="flex flex-col items-center justify-center space-y-[1px]">
          <img @click="setZoom('add')" alt="Zoom In" title="Zoom In" data-bs-toggle="tooltip" data-bs-placement="right"
            src="@/assets/images/icon/zoom-plus.png"
            class="w-[29px] h-[29px] cursor-pointer"
          />
          <img @click="setZoom('min')" alt="Zoom Out" title="Zoom Out" data-bs-toggle="tooltip"
            data-bs-placement="right" src="@/assets/images/icon/zoom-min.png"
            class="w-[29px] h-[29px] cursor-pointer"
          />
        </div>
      </div>
       <div class="flex flex-col items-center justify-center custom-control-fullscreen">
          <img
            @click="onMinimize"
            alt="Minimize Map"
            title="Minimize Map"
            v-if="isFullscreen"
            src="@/assets/images/icon/minimize.png"
          />
        </div>
    </l-control>
    
    <l-marker v-for="(item, index) in markers" :key="index" :lat-lng="item.latLng">
      <l-tooltip v-if="item.name !== ''">
        <div>
          {{ item.name }}
        </div>
      </l-tooltip>
      <l-icon :icon-size="dynamicSize" :icon-anchor="dynamicAnchor">
        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
          <circle cx="7.5" cy="7.5" r="7.5" 
          :fill="
          item.icon === 'LightsOffline' ? '#FF0000' :
          item.icon === 'LightsOnlineOff' ? '#1DAEEF' :
          item.icon === 'LightsOnlineOn' ? '#218863' : undefined
          " />
        </svg>
      </l-icon>
    </l-marker>
  </l-map>
</template>

<script>
import { Tooltip } from "bootstrap/dist/js/bootstrap.esm.min.js";
import "leaflet/dist/leaflet.css";
import { latLngBounds } from "leaflet";
import L from 'leaflet';
import {
  LMap,
  LMarker,
  LTileLayer,
  LTooltip,
  LIcon,
  LControl,
} from "@vue-leaflet/vue-leaflet";

const Leaflet = L || window.L;

export default {
  props: {
    markers: {
      type: Array,
      required: true,
      default: () => [],
      validator: function (value) {
        return value.every(
          (marker) => !isNaN(marker.lat) && !isNaN(marker.long)
        );
      },
    },
    isFullscreen: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LTooltip,
    LIcon,
    LControl,
  },
  watch: {
    markers: {
      immediate: true,
      handler() {
        if (this.mapReady) {
          this.fitMapToMarkers();
        }
      },
      deep: true,
    },
  },
  computed: {
    dynamicAnchor() {
      return [this.iconSquareSize / 2, this.iconSquareSize];
    },
    dynamicSize() {
      return [this.iconSquareSize, this.iconSquareSize];
    },
  },
  data() {
    return {
      iconSquareSize: 15,
      // Use Leaflet.latLngBounds from the imported L or global L
      bounds: Leaflet.latLngBounds(
        { lat: 2.9284, lng: 101.68424 },
        { lat: 2.928236, lng: 101.48424 }
      ),
      mapReady: false,
      simple: {
        url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
        subdomains: 'abcd',
        zoom: 15,
        minZoom: 3,
        maxZoom: 20,
        center: [2.928236, 101.68424],
        marker: [3.140853, 101.693207],
      },
      legends: [],
    };
  },
  methods: {
    onMinimize() {
      this.$emit("on-minimize");
    },
    setZoom(mode) {
      if (!this.$refs.lightMap) return;
      const map = this.$refs.lightMap.leafletObject;
      const addOrMin = mode === "add" ? 1 : -1;
      map.setZoom(map.getZoom() + addOrMin);
    },
    async fitMapToMarkers() {
      if (this.markers && this.markers.length && this.mapReady) {
        // Use Leaflet.latLngBounds from the imported L or global L
        const bounds = Leaflet.latLngBounds(
          this.markers.map((marker) => marker.latLng)
        );
        if (!this.$refs.lightMap) return;
        const map = this.$refs.lightMap.leafletObject;
        if (map) {
          map.invalidateSize(); // Recalculate the map size
          map.fitBounds(bounds);
        } else {
          console.error("Map object not found");
        }
      }
    },
    onMapReady() {
      this.mapReady = true;
      this.fitMapToMarkers();
    },
    handleMapShown() {
      // Ensure the map object is available
      if (!this.$refs.lightMap) return;
      const map = this.$refs.lightMap.leafletObject;
      if (map) {
        map.invalidateSize(); // Recalculate the map size
        this.fitMapToMarkers(); // Refit map bounds to markers
      } else {
        console.error("Map object is not ready yet");
      }
    },
  },
  mounted() {
    // The timeout logic is kept as it might be necessary for map rendering in complex layouts
    setTimeout(() => {
      this.handleMapShown();
    }, 1500);
    
    new Tooltip(document.body, {
      selector: "[data-bs-toggle='tooltip']",
    });
  },
  unmounted() {
    let popovers = document.getElementsByClassName("tooltip");
    Array.from(popovers).forEach((item) => {
      item.remove();
    });
  },
};
</script>

<style>
.leaflet-div-icon {
  border: none !important;
  background: transparent !important;
}
</style>